/**
 * All-in / Capital formula tooltips for Fleet Cost Assumptions.
 */

import { CircleHelp } from 'lucide-react';
import type {
  FleetCostAssumptionsConfig,
  FleetCostVehicleType,
  TrailerStandingAssumptions,
} from '../../types/fleetCostAssumptions';
import {
  ACQUISITION_METHOD_LABELS,
  ACQUISITION_METHODS,
  acquisitionMethodsInUse,
  acquisitionMixCounts,
  capitalAnnualForMethodGbp,
  capitalDepreciableGbp,
  capitalResidualPct,
  RENTAL_PERIOD_LABELS,
  rentalPeriodOf,
  vehicleCapitalAnnualisedGbp,
} from '../../utils/fleetCapitalAnnualised';
import type { FleetCostAcquisitionMethod } from '../../types/fleetCostAssumptions';
import { displayFleetMoneyAmount, formatFleetMoneyK } from '../../utils/fleetCostFormatMoney';
import { fleetStandingAnnualGbp } from '../../utils/fleetCostStandingPerDay';
import {
  trailerStandingAllocatedAnnualPerVehicleGbp,
  trailerStandingFromConfig,
} from '../../utils/fleetCostTrailerStanding';

function allInBreakdown(
  v: FleetCostVehicleType,
  trailer?: TrailerStandingAssumptions,
): {
  residualPct: number;
  depreciable: number;
  life: number;
  capitalPerYear: number;
  opsPerYear: number;
  trailerSharePerYear: number;
  allIn: number;
} {
  const capitalPerYear = vehicleCapitalAnnualisedGbp(v);
  const life = Math.max(1, v.serviceLife || 1);
  const opsPerYear = fleetStandingAnnualGbp(v);
  const t = trailer
    ? trailerStandingFromConfig({ trailer_standing: trailer } as FleetCostAssumptionsConfig)
    : null;
  const trailerSharePerYear =
    t && t.allocation !== 'none' && v.name === t.allocate_to_tonnage_class
      ? trailerStandingAllocatedAnnualPerVehicleGbp(t)
      : 0;
  return {
    residualPct: capitalResidualPct(v),
    depreciable: capitalDepreciableGbp(v),
    life,
    capitalPerYear,
    opsPerYear,
    trailerSharePerYear,
    allIn: capitalPerYear + opsPerYear + trailerSharePerYear,
  };
}

/**
 * All-in £/yr as displayed: sum of rounded parts so the headline matches the tip.
 */
export function allInAnnualDisplayed(
  v: FleetCostVehicleType,
  trailer?: TrailerStandingAssumptions,
): number {
  const b = allInBreakdown(v, trailer);
  return (
    displayFleetMoneyAmount(b.capitalPerYear)
    + displayFleetMoneyAmount(b.opsPerYear)
    + displayFleetMoneyAmount(b.trailerSharePerYear)
  );
}

export function AllInSummaryTip({
  vehicle,
  trailer,
}: {
  vehicle: FleetCostVehicleType;
  trailer?: TrailerStandingAssumptions;
}) {
  const b = allInBreakdown(vehicle, trailer);
  const capitalYrShown = displayFleetMoneyAmount(b.capitalPerYear);
  const opsYrShown = displayFleetMoneyAmount(b.opsPerYear);
  const trailerYrShown = displayFleetMoneyAmount(b.trailerSharePerYear);
  const allInShown = capitalYrShown + opsYrShown + trailerYrShown;
  return (
    <span className="relative inline-flex group/tip align-middle ml-1">
      <CircleHelp
        className="w-3.5 h-3.5 text-muted-foreground group-hover/tip:text-success cursor-help transition-colors"
        aria-hidden
      />
      <span className="sr-only">How all-in £/yr is calculated</span>
      <div
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-dropdown mt-2 w-64 rounded border border-accent bg-popover p-3 text-xs text-foreground shadow-lg opacity-0 transition-opacity group-hover/tip:opacity-100"
      >
        <p className="font-semibold text-success mb-1.5">{vehicle.name} — all-in £/yr</p>
        <p className="text-foreground leading-relaxed">
          <CapitalPartText vehicle={vehicle} residualPct={b.residualPct} depreciable={b.depreciable} life={b.life} />{' '}
          ={' '}
          <span className="text-warning font-mono">{formatFleetMoneyK(capitalYrShown)}</span>/yr
        </p>
        <p className="text-foreground leading-relaxed mt-1">
          + Standing costs{' '}
          <span className="text-warning font-mono">{formatFleetMoneyK(opsYrShown)}</span>/yr
        </p>
        {trailerYrShown > 0 ? (
          <p className="text-foreground leading-relaxed mt-1">
            + Trailer standing share{' '}
            <span className="text-warning font-mono">{formatFleetMoneyK(trailerYrShown)}</span>/yr
          </p>
        ) : null}
        <p className="text-foreground font-medium mt-2 pt-2 border-t border-border">
          = <span className="text-success font-mono">{formatFleetMoneyK(allInShown)}</span> all-in /
          year
        </p>
      </div>
    </span>
  );
}

/** First line of the all-in tip: the capital formula for this class's acquisition method. */
function CapitalPartText({
  vehicle,
  residualPct,
  depreciable,
  life,
}: {
  vehicle: FleetCostVehicleType;
  residualPct: number;
  depreciable: number;
  life: number;
}) {
  const methods = acquisitionMethodsInUse(vehicle);
  if (methods.length > 1) {
    const { counts, total } = acquisitionMixCounts(vehicle);
    return (
      <>
        Average over {total} vehicles:{' '}
        {ACQUISITION_METHODS.filter((m) => counts[m] > 0)
          .map(
            (m) =>
              `${counts[m]} ${ACQUISITION_METHOD_LABELS[m].toLowerCase()} at ${formatFleetMoneyK(capitalAnnualForMethodGbp(vehicle, m))}`,
          )
          .join(', ')}
      </>
    );
  }
  const method: FleetCostAcquisitionMethod = methods[0] ?? 'purchase';
  if (method === 'lease') {
    const term = Math.max(1, vehicle.leaseTermMonths ?? 12);
    const oneOff = (vehicle.leaseUpfront ?? 0) + (vehicle.leaseEndFees ?? 0);
    return (
      <>
        Lease {formatFleetMoneyK(vehicle.leaseMonthly ?? 0)}/mo × 12
        {oneOff > 0 ? <> + one-off {formatFleetMoneyK(oneOff)} over {term} mo</> : null}
      </>
    );
  }
  if (method === 'rental') {
    return (
      <>
        Rental {formatFleetMoneyK(vehicle.rentalRate ?? 0)} {RENTAL_PERIOD_LABELS[rentalPeriodOf(vehicle)]}, every day
        of the year
      </>
    );
  }
  return (
    <>
      (Purchase + fees − {residualPct}% residual) {formatFleetMoneyK(depreciable)} ÷ {life} yr
    </>
  );
}

/** Tip for Capital Total (£/yr/veh). Opens upward so it is not clipped by the table edge. */
export function CapitalTotalFormulaTip() {
  return (
    <span className="relative inline-flex group/tip align-middle ml-1">
      <CircleHelp
        className="w-3.5 h-3.5 text-success/70 group-hover/tip:text-success cursor-help transition-colors"
        aria-hidden
      />
      <span className="sr-only">How Total £/yr/veh is calculated</span>
      <div
        role="tooltip"
        className="pointer-events-none absolute left-0 bottom-full z-dropdown mb-2 w-72 rounded border border-accent bg-popover p-3 text-left text-xs font-normal normal-case tracking-normal text-foreground shadow-lg opacity-0 transition-opacity group-hover/tip:opacity-100"
      >
        <p className="font-semibold text-success mb-1.5">Total (£/yr/veh)</p>
        <p className="text-foreground leading-relaxed">
          <span className="text-foreground">Purchase:</span>{' '}
          <span className="font-mono text-xs">(price + fees − residual% × price) ÷ useful life</span>
        </p>
        <p className="text-foreground leading-relaxed mt-1">
          <span className="text-foreground">Lease:</span>{' '}
          <span className="font-mono text-xs">monthly × 12 + (upfront + end fees) ÷ term in years</span>
        </p>
        <p className="text-foreground leading-relaxed mt-1">
          <span className="text-foreground">Rental:</span>{' '}
          <span className="font-mono text-xs">rate × periods per year (365 days, 52.14 weeks, 12 months)</span>
        </p>
        <p className="text-muted-foreground leading-relaxed mt-2">
          Residual % applies to purchase price only. Rental is charged every calendar day, used or not.
          When a class is split, Total is the average per vehicle weighted by the counts.
        </p>
      </div>
    </span>
  );
}
